<script setup lang="ts">
import type { ChatStatus, UIMessage } from 'ai'

const props = defineProps<{
  messages: UIMessage[]
  status: ChatStatus | undefined
  chatId?: string
}>()

const emit = defineEmits<{
  messageRegenerateRequested: []
}>()

const { t } = useI18n()

const lastMessage = computed(() => props.messages.at(-1))

const messageRefs = ref(new Map<string, HTMLElement>())
const registerMessageRef = (id: string, ref: ComponentPublicInstance | null) => {
  const elInstance = ref?.$el
  if (elInstance)
    messageRefs.value.set(id, elInstance)
}

const scrollToMessage = (id: string) => {
  const messageRef = messageRefs.value.get(id)
  if (messageRef)
    messageRef.scrollIntoView({ behavior: 'smooth' })
}

watch(() => props.status, (status) => {
  if (status !== 'submitted')
    return

  const lastMessage = props.messages[props.messages.length - 1]
  if (!lastMessage || lastMessage.role !== 'user')
    return

  nextTick(() => {
    scrollToMessage(lastMessage.id)
  })
})

const internalMessages = computed(() => props.messages.map((message, index) => ({
  ...message,
  isLastMessage: message.role === 'user'
    ? message.id === lastMessage?.value?.id && ['ready', 'error'].includes(props.status ?? '' as ChatStatus)
    : (index !== 0 && message.id === lastMessage?.value?.id),
})))

const onMessageReady = (message: UIMessage) => void scrollToMessage(message.id)
</script>

<template>
  <div class="relative flex max-h-[50dvh] flex-col gap-3.5 overflow-y-auto scroll-smooth p-3.5">
    <p class="mx-auto mb-10 w-3/4 text-center text-xs text-gray-400">
      {{ t('aiChat.info') }}
    </p>

    <template
      v-for="message in internalMessages"
      :key="message.id"
    >
      <ChatAssistantMessage
        v-if="message.role === 'assistant'"
        :ref="(el) => registerMessageRef(message.id, el as ComponentPublicInstance)"
        :message
        :is-last-message="message.isLastMessage"
        :chat-id
        :status
        @message-ready="onMessageReady(message)"
      />
      <ChatUserMessage
        v-else
        :ref="(el) => registerMessageRef(message.id, el as ComponentPublicInstance)"
        :show-resend-button="message.isLastMessage"
        :message
        @message-regenerate-requested="emit('messageRegenerateRequested')"
      />
    </template>

    <div class="flex items-center justify-center">
      <slot name="bottom" />
    </div>
  </div>
</template>
